import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { camelCase, debounce, startCase } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { FlagService } from '../../flag/flag.service';
import { QuestionService } from '../../question/question.service';
import { SettingService } from '../../setting/setting.service';
import { dateAsString, dateTimeAsString } from '../../shared/date.helper';
import { Answer, Inspection, InspectionItem, ItemInfo, Question, Section } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { VersionService } from '../../version/version.service';
import { AddFlagComponent } from '../add.flag/add.flag.component';
import { AnswerFormComponent } from '../answer-form/answer-form.component';
import { AnswerPicturesComponent } from '../answer-pictures/answer-pictures.component';
import { AnswerService } from '../answer.service';
import { AnswerStatus } from '../answer.status';
import { EditFlagComponent } from '../edit.flag/edit.flag.component';
import { InspectionService } from '../inspection.service';
import { InspectionStatus, statusAsText } from '../inspection.status';
import { RateInspectionComponent } from '../rate-inspection/rate-inspection.component';
import { InspectionFilters, ReviewNavigationComponent } from '../review.navigation/review.navigation.component';

@Component({
  selector: 'app-inspection-review',
  templateUrl: './inspection-review.component.html',
  styleUrls: ['./inspection-review.component.scss'],
})
export class InspectionReviewComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  constructor(
    private inspectionService: InspectionService,
    private questionService: QuestionService,
    private answerService: AnswerService,
    private versionService: VersionService,
    private flagService: FlagService,
    private userService: UserService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private dialog: DialogService,
    private router: Router,
    public settingService: SettingService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    // not to loose popup reference even after navigating
    (window as any).getPopupReference = w => {
      this.popup = w;
      if (this.question) {
        this.changePopup(this.question.answer);
      }
    };
    (window as any).answerChange = w => {
      this.answer = w;
      this.answer.pictures.forEach(p => (p.updatedAt = new Date().getTime()));
      this.question.answer = w;
      this.getFlagsAndComments();
      this.changeDetector.detectChanges();
      if (!this.forceMenu) {
        this.answerChange(w, false);
      }
    };
    // this._questionFilter = getCurrentMandatoryReviewFilter() || 'mandatory';
  }

  popupClosed$ = new Observable<boolean>(observer => {
    const checkPopup = () => {
      if (this.popup && this.popup.closed) {
        observer.next(true);
        observer.complete();
      }
    };
    const intervalId = setInterval(checkPopup, 500);
    return () => clearInterval(intervalId);
  });

  _popupClosed: boolean;
  get popupClosed() {
    return this._popupClosed;
  }

  get selectedItemIndex() {
    return this._selectedItemIndex;
  }
  set selectedItemIndex(i) {
    this._selectedItemIndex = Math.max(0, Math.min(this.items.length - 1, i));
    this.sections = this.items[this._selectedItemIndex].sections;
    this.selectedSectionIndex = this.findFirstShowIndex(this.sections);
  }
  get selectedSectionIndex() {
    return this._selectedSectionIndex;
  }

  set selectedSectionIndex(i) {
    this._selectedSectionIndex = Math.max(0, Math.min(this.sections.length - 1, i));
    this.questions = this.sections[this._selectedSectionIndex].questions;
    this.selectedQuestionIndex = this.findFirstShowIndex(this.questions);
  }

  findFirstShowIndex(array: any[]): number {
    return array.findIndex(obj => obj.show === true);
  }

  get selectedQuestionIndex() {
    return this._selectedQuestionIndex;
  }

  set selectedQuestionIndex(i) {
    this._selectedQuestionIndex = Math.max(0, Math.min(this.questions.length - 1, i));
    this.question = this.questions[this._selectedQuestionIndex];
    if (this.question) {
      // This may change a few times in a short period of time, but only the final value matters
      this.debouncedOnSelectQuestionChange();
    }
  }

  get showPictures() {
    this._popupClosed = this.popup ? this.popup.closed : true;

    let nPictures = 0;
    if (Array.isArray(this.answer)) {
      nPictures = this.answer.reduce((acc, curr) => acc + (curr.pictures ? curr.pictures.length : 0), 0);
    } else {
      nPictures = this.answer && this.answer.pictures ? this.answer.pictures.length : 0;
    }

    return nPictures && !this.forceMenu && (!this.popup || this.popup.closed);
  }

  get answersReadOnly() {
    if (this.inspection && this.inspection.status === InspectionStatus.NEEDS_REVIEW) {
      return false;
    }
    return true;
  }

  get totalRejected() {
    if (Array.isArray(this.answers)) {
      return this.answers.filter(a => a.status === AnswerStatus.REJECTED).length;
    }
    return 0;
  }

  get questionCount() {
    if (this.questions) {
      return this.questions.filter(q => q.show).length;
    }
    return 0;
  }

  get sectionCount() {
    if (this.sections) {
      return this.sections.filter(s => s.show).length;
    }
    return 0;
  }

  get itemCount() {
    if (this.items) {
      return this.items.filter(i => i.show).length;
    }
    return 0;
  }
  get contentRight() {
    return this._contentRight;
  }
  set contentRight(x) {
    this._contentRight = x;
    // update stats
    this.getFlagsAndComments();
    this.updateFilteredFlags();
    this.updateFilteredComments();

    // wait for it to render
    setTimeout(() => {
      switch (x) {
        case 'comments':
          const commentsDiv = document.getElementById('allComments');

          if (commentsDiv) {
            const sectionDiv = Array.from(commentsDiv.getElementsByClassName('comment-section')).find(div => {
              const item = this.items[this.selectedItemIndex];
              const section = this.sections[this.selectedSectionIndex];
              const label = `${item.label} / ${section.name}`;
              return div.attributes['section'].value === label;
            });

            if (sectionDiv) {
              const comment = Array.from(sectionDiv.getElementsByClassName('comment-info')).find(
                div => div.attributes['question'].value === this.question.text,
              );

              if (comment) {
                comment.scrollIntoView();
              }
            }
          }
          break;

        case 'flags':
          const flagsDiv = document.getElementById('allFlags');

          if (flagsDiv) {
            const flagSectionDiv = Array.from(flagsDiv.getElementsByClassName('flag-section')).find(div => {
              const item = this.items[this.selectedItemIndex];
              const section = this.sections[this.selectedSectionIndex];
              const label = `${item.label} / ${section.name}`;
              return div.attributes['section'].value === label;
            });

            if (flagSectionDiv) {
              const flag = Array.from(flagSectionDiv.getElementsByClassName('flag-info')).find(
                div => div.attributes['question'].value === this.question.text,
              );

              if (flag) {
                flag.scrollIntoView();
              }
            }
          }
          break;

        case 'overview':
          // Reload inspection, in case flagging score has changed due to new/changed flags
          this.inspectionService.get(this.inspection.id).then(refreshedInspection => {
            this.inspection.flaggingScore = refreshedInspection.flaggingScore;
          });
      }
    }, 500);
  }
  get showOnlyInspectorsComments() {
    return this._showOnlyInspectorsComments;
  }
  set showOnlyInspectorsComments(filter) {
    this._showOnlyInspectorsComments = filter;
    this.updateFilteredComments();
  }
  get showOnlyFlagsWithPictures() {
    return this._showOnlyFlagsWithPictures;
  }
  set showOnlyFlagsWithPictures(filter) {
    this._showOnlyFlagsWithPictures = filter;
    this.updateFilteredFlags();
  }

  @Input() inspection: Inspection;
  @Input() filters: InspectionFilters;
  @Input() navigation: ReviewNavigationComponent;
  @Output() onPopupClosed = new EventEmitter<void>();
  @Output() onPopupOpened = new EventEmitter<void>();
  @Output() refreshAnswers = new EventEmitter<void>();

  dateAsString = dateAsString;
  dateTimeAsString = dateTimeAsString;
  statusAsText = statusAsText;
  InspectionStatus = InspectionStatus;
  nLocations = false;

  public AnswerStatus = AnswerStatus;
  private subscriptions: Subscription = new Subscription();

  forceMenu = false;
  items: Array<InspectionItem & { info?: ItemInfo; label?: string; show?: boolean; index?: number }> = [];
  pictures: any[];
  answer: any;
  canForceChange: boolean = true;
  _selectedItemIndex: number;

  sections = [];

  _selectedSectionIndex: number;

  questions = [];

  _selectedQuestionIndex: number;

  question = null;

  @Input() answers;

  isNotLatestVersion = false;

  popup: Window;

  @ViewChildren('answerPictures') answerPictures: QueryList<AnswerPicturesComponent>;
  @ViewChildren('questionDiv') questionDiv: QueryList<ElementRef>;
  @ViewChild('wrapper', { static: true }) private wrapper: ElementRef;
  @ViewChild('rating', { static: true }) rating: RateInspectionComponent;
  @ViewChild('addFlagModal', { static: false }) addFlagModal: AddFlagComponent;
  @ViewChild('editFlagModal', { static: false }) editFlagModal: EditFlagComponent;
  @ViewChild('answerForm', { static: false }) answerForm: AnswerFormComponent;

  _contentRight: 'question' | 'flag' | 'flags' | 'comments' | 'overview' = 'question';

  allFlags = [];
  allComments = [];
  totalFlags = 0;
  totalComments = 0;

  // This may be called a few times in a short period of time, but only the final value matters
  debouncedOnSelectQuestionChange = debounce(this.onSelectedQuestionChange, 10);

  filteredComments = [];
  _showOnlyInspectorsComments = false;

  filteredFlags = [];
  _showOnlyFlagsWithPictures = false;

  initialScreen;
  scoresEnabled = true;
  destroyed;

  canEditFlagDetails = false;
  isForceMenuEnabled;
  editingFlag;
  selectedType: 'item' | 'section' | 'question' = 'item';
  navigatedByArrow: 'left' | 'right' = null;
  onClickItem(i) {
    this.selectedItemIndex = i;
    this.selectedType = 'item';
  }

  onClickSection(i) {
    this.selectedSectionIndex = i;
    this.selectedType = 'section';
  }

  onClickQuestion(i) {
    this.navigatedByArrow = null;
    this.selectedQuestionIndex = i;
    this.selectedType = 'question';
  }

  updateFilteredComments() {
    if (this.showOnlyInspectorsComments) {
      // shallow copy so we don't mess with the current arrays
      const ret = this.allComments.map(c => ({ ...c }));

      // actual filters
      ret.forEach(section => {
        section.comments = section.comments.filter(c => c.userId === this.inspection.inspectorId);
      });
      this.filteredComments = ret.filter(s => s.comments.length);
    } else {
      this.filteredComments = this.allComments;
    }
  }

  updateFilteredFlags() {
    if (this.showOnlyFlagsWithPictures) {
      // shallow copy so we don't mess with the current arrays
      const ret = this.allFlags.map(section => ({ ...section }));

      // actual filters
      ret.forEach(section => {
        section.flags = section.flags.filter(f => {
          return f.hasPictures;
        });
      });

      this.filteredFlags = ret.filter(s => s.flags.length);
    } else {
      this.filteredFlags = this.allFlags;
    }
  }

  /**
   * @param preserveRightPanel When true, do not switch the right sidebar to Question (e.g. when opening
   *   edit-flag for a pictured flag from the Flags tab — otherwise flags view is hidden behind the modal).
   */
  async onSelectedQuestionChange(preserveRightPanel = false) {
    // this debounced method was running after the component had been destroyed
    if (this.destroyed) {
      return;
    }

    // If not supposed to show, go to next question
    if (!this.question.show) {
      this.next();
      return;
    }

    // If not already loaded, load related questions
    if (!this.question.relatedLoaded) {
      this.question.relatedLoaded = true;
      await this.loadRelated();
    }

    this.resetQuestion();
    this.answerSetPictures(this.question.answer);
    // Ensure related pictures are shown
    if (this.popup && !this.popup.closed) {
      this.popup.location.reload();
    }

    this.changePopup(this.question.answer);

    if (!preserveRightPanel) {
      // Set initial right panel ('comments', 'flags', 'overview', 'question'). Defaults to 'question'.
      this.contentRight = this.initialScreen || 'question';
      this.initialScreen = undefined;

      this.router.navigate(['/inspection/review/main', this.inspection.id], {
        queryParamsHandling: 'merge',
        queryParams: {
          item: this.selectedItemIndex,
          section: this.selectedSectionIndex,
          question: this.selectedQuestionIndex,
          right: 'question',
        },
      });
    }

    this.getFlagsAndComments();

    if (this.questionDiv && this.questionDiv.first && this.questionDiv.first.nativeElement) {
      this.questionDiv.first.nativeElement.scrollTop = 0;
    }

    // update flags
    const answers = Array.isArray(this.question.answer) ? this.question.answer : [this.question.answer];

    answers.forEach(answer => {
      const pictureIds = answer.pictures.map(p => p.id);
      answer.flags = this.allFlags
        .reduce((all, curr) => [...all, ...curr.flags], [])
        .filter(f => f.answerId === answer.id || pictureIds.includes(f.pictureId))
        .map(f => f.flag)
        // remove duplicates by id
        .filter((f, i, self) => self.findIndex(t => t.id === f.id) === i);
    });
  }

  async loadRelated() {
    this.question.related = await this.questionService.getRelated(
      this.question.id,
      this.question.version,
      this.inspection.versionId,
    );
    this.setRelatedAnswers();
  }

  async ngOnInit() {
    // Set initial right panel ('comments', 'flags', 'overview', 'question'). Defaults to 'question'.
    this.route.queryParams.subscribe(query => {
      if (!this.initialScreen) {
        this.initialScreen = query.right;
      }
    });

    this.scoresEnabled = await this.settingService.scoresEnabled();
    this.canEditFlagDetails = await this.userService.hasPermissions([permissions.FLAG.WRITE_DETAILS]);
    this.isForceMenuEnabled = await this.settingService.forceMenuEnabled();

    //if popup is opened, disable the button on Control Tool
    if (this.popup && !this.popup.closed) {
      this.onPopupOpened.emit();
    }
  }

  ngOnDestroy() {
    // Avoid debounced method running after the component has been destroyed
    this.destroyed = true;
    this.subscriptions.unsubscribe();
  }

  ngAfterViewChecked(): void {
    try {
      this.changeDetector.detectChanges();
    } catch (e) {}
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('answers' in changes) {
      if (this.answers) {
        this.load();
        this.nLocations = this.answers.filter(a => a.location).length;
      }
    }
    if ('filters' in changes) {
      // If filters change, show list of items/sections/questions

      if (!this.canForceChange) {
        this.forceMenu = false;
        this.canForceChange = true;
      } else if (this.selectedType !== 'question') {
        this.forceMenu = true;
      }

      this.setQuestionsVisibility();
      this.setSectionsVisibility();
      this.setItemsVisibility();
      this.updateProgressBars();
    }
  }

  async load() {
    await this.loadItems();
    await this.mergeAnswers();
    this.getFlagsAndComments();
    this.setQuestionsVisibility();
    this.setSectionsVisibility();
    this.setItemsVisibility();

    // const latest = (await this.versionService.latest()).id;
    // this.isNotLatestVersion = this.inspection.versionId !== latest;
    this.isNotLatestVersion = false;

    this.route.queryParams.subscribe(params => {
      this.selectedItemIndex = parseInt(params.item || 0, 10);
      this.selectedSectionIndex = parseInt(params.section || 0, 10);
      this.selectedQuestionIndex = parseInt(params.question || this.findFirstShowIndex(this.questions), 10);
    });

    this.updateProgressBars();
    this.resetQuestion();
    this.updateFilteredComments();
    this.updateFilteredFlags();
  }

  updateProgressBars() {
    this.items.forEach(i => {
      i.sections.forEach(s => this.setSectionProgress(s));
      this.setItemProgress(i);
    });
  }

  refreshFlags() {
    this.refreshAnswers.emit();
  }

  // tslint:disable:forin
  getFlagsAndComments() {
    this.totalFlags = 0;
    this.totalComments = 0;
    this.allFlags = [];
    this.allComments = [];

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        const sectionFlags = [];
        const sectionComments = [];

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          const flags = this.getFlags(question);
          const comments = this.getComments(question);

          sectionFlags.push(
            ...flags.map(f => ({
              ...f,
              flag: f,
              question: question.text,
              hasPictures: this.nPictures(question),
              // AI-1178: "We also need to show sequential numbering for flags"
              index: ++this.totalFlags,
            })),
          );

          sectionComments.push(
            ...comments.map(f => ({
              ...f,
              question: question.text,
            })),
          );
        }

        if (sectionFlags.length) {
          this.allFlags.push({
            label: `${item.label} / ${section.name}`,
            flags: sectionFlags,
          });
        }

        if (sectionComments.length) {
          this.totalComments += sectionComments.length;

          this.allComments.push({
            label: `${item.label} / ${section.name}`,
            comments: sectionComments,
          });
        }
      }
    }
  }

  async loadItems() {
    this.items = [];

    const items: Array<InspectionItem & { info?: ItemInfo; label?: string; show?: boolean; index?: number }> = (
      await this.versionService.tree(this.inspection.versionId)
    ).items;

    // filter empty sections
    items.forEach(item => {
      item.sections = item.sections.filter(section => section.questions.length);
    });

    // filter empty items
    items.forEach((item, i) => {
      if (!item.sections.length) {
        delete items[i];
      } else {
        item.info = this.inspection.items.find(info => info.itemId === item.id);
      }
    });

    items.forEach(item => {
      if (!item.info) {
        return;
      }

      if (item.info.quantity === 1) {
        const customName = item.info.names && item.info.names[0];
        const defaultName = item.name;
        const label = customName ? `${customName} (${defaultName})` : defaultName;

        this.items.push({
          ...item,
          label,
        });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          const customName = item.info.names && item.info.names[i];
          const defaultName = `${item.name} # ${i + 1}`;
          const label = customName ? `${customName} (${defaultName})` : defaultName;

          this.items.push({
            // deep copy
            ...JSON.parse(JSON.stringify(item)),
            label,
            index: i + 1,
          });
        }
      }
    });
  }

  // tslint:disable: forin
  async mergeAnswers() {
    const subtypeInfo = await this.flagService.subtypeInfo;

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];

          if (question.repeat && question.repeat.length) {
            question.repeat.forEach(() => {
              const filteredAnswers = this.answers.filter(ans => {
                return ans.questionId === question.id;
              });

              question.answer = filteredAnswers;
            });
          } else {
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });

            if (answer) {
              question.answer = answer;
            }
          }

          const answers = Array.isArray(question.answer) ? question.answer : [question.answer];

          answers.forEach(answer => {
            if (answer) {
              answer.question = question;

              // set images' path for tooltip and deeplink
              answer.pictures.forEach(pic => {
                pic.path = `${item.label} / ${section.name} / ${question.text}`;
                pic.deeplink = [+iItem, +iSection, +iQuestion];
              });

              // set flags & comments' deeplink
              [...answer.flags, ...answer.comments].forEach((x: any) => {
                x.deeplink = [+iItem, +iSection, +iQuestion];
              });

              // set flags subtype names
              answer.flags.forEach((x: any) => {
                if (x.subtype) {
                  x.subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, x.subtype.name).name;
                }
              });
            }
          });
        }
      }
    }
  }

  // tslint:disable: forin
  setQuestionsVisibility() {
    for (const iItem in this.items) {
      const sections = this.items[iItem].sections;

      for (const iSection in sections) {
        const questions: Array<Question & { show?: boolean }> = sections[iSection].questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          question.show = this.shouldShowQuestion(question, sections[iSection], this.items[iItem]);
        }
      }
    }
  }

  shouldShowQuestion(question, section, item) {
    let hasAnswers = Array.isArray(question.answer) ? !!question.answer.length : 'answer' in question;

    if (!hasAnswers) {
      return false;
    }

    let answers = Array.isArray(question.answer) ? question.answer : [question.answer];
    const mandatory = hasAnswers && question.mandatoryReview;

    if (this.filters) {
      if (this.filters.flags && this.filters.flags !== 'all') {
        answers = answers.filter(answer =>
          answer.flags.some(flag => this.filters.flags !== 'all' && this.filters.flags.includes(flag.subtypeId)),
        );
        hasAnswers = answers.length > 0;
        if (!hasAnswers) {
          return false;
        }
      }

      if (!this.filters.items.includes(item.label)) {
        return false;
      }

      if (!this.filters.sections.includes(section.id)) {
        return false;
      }

      const questionTagIds = question.questionTags.map(x => x.id);
      const hasQuestionTags = this.filters.questionTags.length > 0;
      const noQuestionTag = this.filters.questionTags.includes(0);
      const questionHasTags = question.questionTags.length > 0;
      const hasMoreThanOneTag = this.filters.questionTags.length > 1;
      const noMatchingTags = !this.filters.questionTags.some(element => questionTagIds.includes(element));

      if (!hasQuestionTags) {
        return false;
      }

      if (noQuestionTag) {
        if (hasMoreThanOneTag) {
          if (questionTagIds.length > 0 && noMatchingTags) {
            return false;
          }
        } else {
          if (questionHasTags) {
            return false;
          }
        }
      } else {
        if (questionTagIds.length === 0 || (questionTagIds.length > 0 && noMatchingTags)) {
          return false;
        }
      }

      if (this.filters.questions === 'mandatory' && !mandatory) {
        return false;
      }

      const scored = hasAnswers && this.getScore(question);

      if (this.filters.questions === 'scored' && !scored) {
        return false;
      }
      if (this.filters.questions === 'mandatoryOrScored' && !(mandatory || scored)) {
        return false;
      }

      if (this.filters.questions === 'autoScored') {
        if (!scored) {
          return false;
        }
        const flags = answers.reduce((acc, ans) => [...acc, ...ans.flags], []);
        if (!flags.find(x => x.systemFlag)) {
          return false;
        }
      }

      if (!this.filters.withoutFlags) {
        let hasFlags;

        if (Array.isArray(question.answer)) {
          hasFlags = question.answer.find(a => a.flags && a.flags.length);
        } else {
          hasFlags = question.answer.flags && question.answer.flags.length;
        }

        if (!hasFlags) {
          return false;
        }
      }

      if (!this.filters.withoutComments) {
        let hasComments;

        if (Array.isArray(question.answer)) {
          hasComments = question.answer.find(a => a.comments && a.comments.length);
        } else {
          hasComments = question.answer.comments && question.answer.comments.length;
        }

        if (!hasComments) {
          return false;
        }
      }

      if (!this.filters.accepted) {
        let isAccepted;

        if (Array.isArray(question.answer)) {
          isAccepted = question.answer.find(a => a.status === AnswerStatus.ACCEPTED);
        } else {
          isAccepted = question.answer.status === AnswerStatus.ACCEPTED;
        }
        if (isAccepted) {
          return false;
        }
      }

      if (!this.filters.unreviewed) {
        let isNotReviewed;

        if (Array.isArray(question.answer)) {
          isNotReviewed = question.answer.find(a => a.status === AnswerStatus.NOT_REVIEWED);
        } else {
          isNotReviewed = question.answer.status === AnswerStatus.NOT_REVIEWED;
        }

        if (isNotReviewed) {
          return false;
        }
      }

      if (!this.filters.rejected) {
        let isRejected;

        if (Array.isArray(question.answer)) {
          isRejected = question.answer.find(a => a.status === AnswerStatus.REJECTED);
        } else {
          isRejected = question.answer.status === AnswerStatus.REJECTED;
        }

        if (isRejected) {
          return false;
        }
      }

      if (!this.filters.withAnswers) {
        return !this.isPictureOrLocationOnly(question) && question.answer.answer === '';
      }
    }
    return hasAnswers;
  }

  // tslint:disable: forin
  setRelatedAnswers() {
    const question = this.question;
    const item = this.items[this.selectedItemIndex];

    if (Array.isArray(question.related)) {
      if (Array.isArray(question.answer)) {
        question.answer.forEach(answer => {
          answer.related = question.related.map(q => {
            return this.findAnswers(q.id, answer.buildingIndex);
          });
        });
      } else {
        const answer = question.answer;
        if (answer) {
          answer.related = question.related
            .map(q => {
              return this.findAnswers(q.id, item.index);
            })
            .reduce((ret, a) => [...ret, ...a], []);
        }
      }
    }
  }

  // tslint:disable: forin
  findAnswers(questionId, buildingIndex) {
    const answers = [];

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;

      for (const iSection in sections) {
        const questions = sections[iSection].questions;

        for (const iQuestion in questions) {
          const question: Question = questions[iQuestion];
          if (question.answer && question.id === questionId) {
            if (!buildingIndex || !item.index || item.index === buildingIndex) {
              if (Array.isArray(question.answer)) {
                for (const answer of question.answer) {
                  answer.pictures.forEach(pic => {
                    pic.deeplink = [+iItem, +iSection, +iQuestion];
                  });
                }
                answers.push(...question.answer);
              } else {
                question.answer.pictures.forEach(pic => {
                  pic.deeplink = [+iItem, +iSection, +iQuestion];
                });
                answers.push(question.answer);
              }
            }
          }
        }
      }
    }
    return answers;
  }

  // tslint:disable: forin
  setSectionsVisibility() {
    for (const iItem in this.items) {
      const sections: Array<Section & { show?: boolean }> = this.items[iItem].sections;

      for (const iSection in sections) {
        const questions: Array<Question & { show?: boolean }> = sections[iSection].questions;
        sections[iSection].show = !!questions.filter(q => q.show).length;
      }
    }
  }

  // tslint:disable: forin
  setItemsVisibility() {
    for (const iItem in this.items) {
      const sections: Array<Question & { show?: boolean }> = this.items[iItem].sections;
      this.items[iItem].show = !!sections.filter(s => s.show).length;
    }
  }

  resetQuestion() {
    this.answer = this.question && this.question.answer;
    this.contentRight = 'question';
    if (this.selectedType !== 'question') {
      this.forceMenu = !this.getCurrentFlags().length && !this.nTotalPictures(this.question);
    }
  }

  next() {
    //if there is no question to show do nothing
    if (!this.checkShowFields(this.questions)) {
      return;
    }
    this.navigatedByArrow = 'right';
    // Increment question
    if (this.selectedQuestionIndex + 1 < this.questions.length) {
      this.selectedQuestionIndex++;
    } else {
      // Increment section
      if (this.selectedSectionIndex + 1 < this.sections.length) {
        this.selectedSectionIndex++;
      } else {
        // Increment item
        if (this.selectedItemIndex + 1 < this.items.length) {
          this.selectedItemIndex++;
        } else {
          // No questions left
          console.log('the end');

          // Move back to last displayable question
          if (!this.question.show) {
            this.previous();
          }
        }
      }
    }

    // Skip hidden questions
    if (!this.question.show) {
      this.next();
    }

    this.resetQuestion();
  }

  previous() {
    //if there is no question to show do nothing
    if (!this.checkShowFields(this.questions)) {
      return;
    }
    this.navigatedByArrow = 'left';
    // Decrement question
    if (this.selectedQuestionIndex) {
      this.selectedQuestionIndex--;
    } else {
      // Decrement section
      if (this.selectedSectionIndex) {
        this.selectedSectionIndex--;
        this.selectedQuestionIndex = this.sections[this.selectedSectionIndex].questions.length - 1;
      } else {
        // Increment item
        if (this.selectedItemIndex) {
          this.selectedItemIndex--;
          this.selectedSectionIndex = this.items[this.selectedItemIndex].sections.length - 1;
          this.selectedQuestionIndex = this.sections[this.selectedSectionIndex].questions.length - 1;
        } else {
          // No questions left
          console.log('the end');

          // Move back to last displayable question
          if (!this.question.show) {
            this.next();
          }
        }
      }
    }

    // Skip hidden questions
    if (!this.question.show) {
      this.previous();
    }

    this.resetQuestion();
  }

  checkShowFields(questionsArray) {
    return questionsArray.some(question => question.show);
  }

  setSectionProgress(section) {
    let count = 0;
    const visibleQuestions = section.questions.filter(q => q.show);

    visibleQuestions.forEach(q => {
      if (Array.isArray(q.answer)) {
        const allReviewed = q.answer.reduce((acc, curr) => acc && curr.status !== AnswerStatus.NOT_REVIEWED, true);
        if (allReviewed) {
          count++;
        }
      } else {
        if (q.answer.status !== AnswerStatus.NOT_REVIEWED) {
          count++;
        }
      }
    });

    section.progress = (count / visibleQuestions.length) * 100;
  }

  setItemProgress(item) {
    const visibleSections = item.sections.filter(s => s.show);

    if (!visibleSections.length) {
      item.progress = 100;
      return;
    }

    let sum = 0;

    visibleSections.forEach(s => {
      sum += s.progress || 0;
    });

    item.progress = sum / visibleSections.length;
  }

  getInspectionProgress() {
    if (Array.isArray(this.answers)) {
      const reviewed = this.answers.filter(a => a.status !== AnswerStatus.NOT_REVIEWED);
      return (100 * reviewed.length) / this.answers.length;
    }
    return 0;
  }

  getInspectionMandatoryProgress() {
    if (Array.isArray(this.answers)) {
      const mandatory = this.answers.filter(a => a.question && a.question.mandatoryReview);
      const reviewed = mandatory.filter(a => a.status !== AnswerStatus.NOT_REVIEWED);
      return mandatory.length ? (100 * reviewed.length) / mandatory.length : 0;
    }
    return 0;
  }

  answerStatusChange(ev: { answer; status: AnswerStatus }, stay = false) {
    switch (ev.status) {
      case AnswerStatus.ACCEPTED:
        this.accept(ev.answer, stay);
        break;
      case AnswerStatus.REJECTED:
        this.reject(ev.answer);
        break;
    }
  }

  answerSetPictures(answer) {
    answer.pictures = [...(answer.pictures || []), ...this.getRelatedPictures(answer)];
    Object.assign(this.answer, answer);

    if (this.isForceMenuEnabled) {
      this.forceMenu = this.selectedType !== 'question';
    } else {
      this.forceMenu = !this.getCurrentFlags().length && !this.nTotalPictures(this.question);
    }
    this.question.relatedLoaded = false;
  }

  async accept(answer, stay = false) {
    answer.status = AnswerStatus.ACCEPTED;
    this.setSectionProgress(this.sections[this.selectedSectionIndex]);
    this.setItemProgress(this.items[this.selectedItemIndex]);
    await this.answerService.setStatus(answer.id, AnswerStatus.ACCEPTED);
    this.toastr.success('Saved!');
    if (answer.id != null) {
      answer.changes = await this.answerService.getChanges(answer.id);
    }

    await this.updateAnswerLocally(answer);

    if (!stay) {
      this.goToNextUnreviewed();
    }
  }

  private async updateAnswerLocally(answer: any) {
    try {
      const updatedAnswer = await this.answerService.get(answer.id);

      // Preserve any already-attached related pictures locally when refreshing from server
      const previousPictures = answer.pictures || [];
      const previousRelatedPictures = previousPictures.filter(p => p && p.related);

      Object.assign(answer, updatedAnswer);

      const serverPictures = (updatedAnswer && updatedAnswer.pictures) || [];
      const mergedAnswerPictures = [
        ...serverPictures,
        ...previousRelatedPictures.filter(rp => !serverPictures.some(sp => sp && sp.id === rp.id)),
      ];
      answer.pictures = mergedAnswerPictures;

      if (this.answers) {
        const answerIndex = this.answers.findIndex(a => a.id === answer.id);
        if (answerIndex !== -1) {
          const existing = this.answers[answerIndex];
          const existingRelated = (existing.pictures || []).filter(p => p && p.related);
          const mergedPictures = [
            ...serverPictures,
            ...existingRelated.filter(rp => !serverPictures.some(sp => sp && sp.id === rp.id)),
          ];

          this.answers[answerIndex] = {
            ...existing,
            ...updatedAnswer,
            pictures: mergedPictures,
            flags: updatedAnswer.flags || existing.flags,
            comments: updatedAnswer.comments || existing.comments,
            changes: updatedAnswer.changes || existing.changes,
          };
        }
      }

      this.changeDetector.markForCheck();
    } catch (error) {
      console.error('Error updating answer locally:', error);
      this.refreshAnswers.emit();
    }
  }

  goToNextUnreviewed() {
    const next = this.nextUnreviewed();

    if (next) {
      this.selectedItemIndex = next.iItem;
      this.selectedSectionIndex = next.iSection;
      this.selectedQuestionIndex = next.iQuestion;
    } else {
      console.log('All questions were reviewed');
    }
  }

  nextUnreviewed() {
    let startSection = this.selectedSectionIndex;
    let startQuestion = this.selectedQuestionIndex;
    // From current position
    for (let iItem = this.selectedItemIndex; iItem < this.items.length; iItem++) {
      for (let iSection = startSection; iSection < this.items[iItem].sections.length; iSection++) {
        startSection = 0;
        for (
          let iQuestion = startQuestion;
          iQuestion < this.items[iItem].sections[iSection].questions.length;
          iQuestion++
        ) {
          startQuestion = 0;
          const question: Question & { show?: boolean } = this.items[iItem].sections[iSection].questions[iQuestion];
          // Skip hidden questions
          if (!question.show) {
            continue;
          }

          if (!this.questionWasReviewed(question)) {
            return { iItem, iSection, iQuestion };
          }
        }
      }
    }

    // If nothing found, from begining
    for (let iItem = 0; iItem < this.items.length; iItem++) {
      for (let iSection = 0; iSection < this.items[iItem].sections.length; iSection++) {
        for (let iQuestion = 0; iQuestion < this.items[iItem].sections[iSection].questions.length; iQuestion++) {
          const question: Question & { show?: boolean } = this.items[iItem].sections[iSection].questions[iQuestion];
          // Skip hidden questions
          if (!question.show) {
            continue;
          }
          if (!this.questionWasReviewed(question)) {
            return { iItem, iSection, iQuestion };
          }
        }
      }
    }

    // If nothing found, false

    return false;
  }

  questionWasReviewed(question) {
    const answer = question.answer;

    if (!answer) {
      return true;
    }

    if (Array.isArray(answer)) {
      // Check if there is an unreviewed answer in this question
      if (answer.find(a => a.status === AnswerStatus.NOT_REVIEWED)) {
        return false;
      }
      return true;
    }

    return answer.status !== AnswerStatus.NOT_REVIEWED;
  }

  async reject(answer) {
    answer.status = AnswerStatus.REJECTED;
    this.setSectionProgress(this.sections[this.selectedSectionIndex]);
    this.setItemProgress(this.items[this.selectedItemIndex]);
    await this.answerService.setStatus(answer.id, AnswerStatus.REJECTED);
    this.toastr.success('Saved!');
    if (answer.id != null) {
      answer.changes = await this.answerService.getChanges(answer.id);
    }

    await this.updateAnswerLocally(answer);
  }

  questionStatus(q) {
    if (Array.isArray(q.answer)) {
      // Multi answer
      const hasFlags = q.answer.reduce((acc, curr) => acc || (curr.flags && curr.flags.length), false);
      if (hasFlags) {
        return 'flag';
      }

      const hasReject = q.answer.reduce((acc, curr) => acc || curr.status === AnswerStatus.REJECTED, false);
      if (hasReject) {
        return 'rejected';
      }

      const allAccept = q.answer.reduce((acc, curr) => acc && curr.status === AnswerStatus.ACCEPTED, true);
      if (allAccept) {
        return 'accepted';
      }

      return 'default';
    } else {
      // Single answer
      if (q.answer && Array.isArray(q.answer.flags) && q.answer.flags.length) {
        return 'flag';
      }
      if (q.answer.status === AnswerStatus.ACCEPTED) {
        return 'accepted';
      }
      if (q.answer.status === AnswerStatus.REJECTED) {
        return 'rejected';
      }
      return 'default';
    }
  }

  openPopup() {
    this.ngZone.runOutsideAngular(() => {
      this.popup = window.open(location.origin + '/inspection/popup', 'LC+ Pictures', 'width=1400, height=700');
      setTimeout(() => {
        try {
          this.changePopup(this.question.answer);
        } catch {}
      }, 1000);

      this.popupClosed$.pipe(filter(() => !!this.changeDetector)).subscribe(() => {
        this.ngZone.run(() => {
          this._popupClosed = true;
          this.onPopupClosed.emit();
        });
      });
    });
    this.onPopupOpened.emit();
  }

  changePopup(answer: Answer | Answer[]) {
    if (this.popup) {
      let hasPictures = false;
      if (Array.isArray(answer)) {
        hasPictures = answer.some(a => a?.pictures?.length > 0);
      } else if (answer && answer.pictures) {
        hasPictures = answer.pictures.length > 0;
      }
      this.popup.postMessage(
        {
          answer: answer,
          hasPictures: hasPictures,
        },
        '*',
      );
    }
  }

  closePopup() {
    if (this.popup && !this.popup.closed) {
      this.popup.close();
      this.onPopupClosed.emit();
    }
  }

  togglePopup() {
    if (this._popupClosed) {
      this.openPopup();
    } else {
      this.closePopup();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.showPictures && this.answerPictures.first.fullScreen) {
      // Ignore
      return;
    }

    const inputTypes = ['INPUT', 'TEXTAREA', 'SELECT'];
    if (inputTypes.indexOf(document.activeElement.tagName) < 0) {
      switch (event.key) {
        case 'ArrowLeft':
          if (this.showPictures && this.answerPictures.first.hasPrev()) {
            return;
          }
          this.previous();
          break;
        case 'ArrowRight':
          if (this.showPictures && this.answerPictures.first.hasNext()) {
            return;
          }
          this.next();
          break;
        case 'Escape':
          return;
      }
    }
  }

  allAccepted() {
    if (this.question && Array.isArray(this.question.answer)) {
      const answers: Answer[] = this.question.answer;
      return !answers.find(a => a.status !== AnswerStatus.ACCEPTED);
    }
    return false;
  }

  async acceptAll() {
    if (this.question && Array.isArray(this.question.answer)) {
      const answers: Answer[] = this.question.answer;
      const promises = [];
      for (const answer of answers) {
        answer.status = AnswerStatus.ACCEPTED;
        this.setSectionProgress(this.sections[this.selectedSectionIndex]);
        this.setItemProgress(this.items[this.selectedItemIndex]);
        promises.push(this.answerService.setStatus(answer.id, AnswerStatus.ACCEPTED));
      }
      await Promise.all(promises);
      this.toastr.success('Saved!');
      this.goToNextUnreviewed();
    }
  }

  private getRelatedPictures(answer) {
    if (answer.picturesOk) {
      return [];
    }
    answer.picturesOk = true;

    if (answer && Array.isArray(answer.related)) {
      return answer.related.reduce((ret, a) => {
        if (a && Array.isArray(a.pictures)) {
          return [...ret, ...a.pictures.filter(p => !p.related).map(p => ({ ...p, related: true }))];
        }
        return ret;
      }, []);
    }
    return [];
  }

  nPictures(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => acc + cur.pictures.filter(p => !p.related).length, 0);
      } else {
        return q.answer.pictures.filter(p => !p.related).length;
      }
    }
    return 0;
  }

  nTotalPictures(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => acc + cur.pictures.length, 0);
      } else {
        return q.answer.pictures.length;
      }
    }
    return 0;
  }

  nComments(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => acc + cur.comments.length, 0);
      } else {
        return q.answer.comments.length;
      }
    }
    return 0;
  }

  nFlags(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => acc + cur.flags.length, 0);
      } else {
        return q.answer.flags.length;
      }
    }
    return 0;
  }

  getFlags(q) {
    // Ignoring related
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, ans) => [...acc, ...ans.flags.filter(f => f.answerId === ans.id)], []);
      } else {
        return q.answer.flags.filter(f => f.answerId === q.answer.id);
      }
    }
    return [];
  }

  getComments(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => [...acc, ...cur.comments], []);
      } else {
        return q.answer.comments;
      }
    }
    return [];
  }

  gotoQuestion(flag) {
    const indexes = flag.deeplink;

    const item = indexes[0];
    const section = indexes[1];
    const question = indexes[2];

    this.selectedItemIndex = item;
    this.selectedSectionIndex = section;
    this.selectedQuestionIndex = question;
    this.resetQuestion();

    /**
     * AI-1527/AI-1676 https://aruinspection.atlassian.net/browse/AI-1527
     * Check if target question is visible. If it's hidden, change filter
     */
    if (
      this.filters &&
      !(this.items[item].sections[section].questions[question] as Question & { show: boolean }).show
    ) {
      this.navigation.clearFilters();
    }

    if (flag.pictureId) {
      this.openPictureFlag(flag);
    }
  }

  getFilteredCommentCount() {
    return this.allComments.reduce(
      (sum, section) => sum + section.comments.filter(c => c.userId === this.inspection.inspectorId).length,
      0,
    );
  }

  getFilteredFlags() {
    if (this.showOnlyFlagsWithPictures) {
      // shallow copy so we don't mess with the current arrays
      const ret = this.allFlags.map(section => ({ ...section }));

      // actual filters
      ret.forEach(section => {
        section.flags = section.flags.filter(f => {
          return f.picture && f.picture.key;
        });
      });

      return ret.filter(s => s.flags.length);
    } else {
      return this.allFlags;
    }
  }

  getImageCount() {
    return this.answers ? this.answers.reduce((sum, answer) => sum + answer.pictures.length, 0) : '';
  }

  async reopen() {
    this.inspection = await this.inspectionService.reopen(this.inspection.id);
  }

  async acceptInspection() {
    this.rating.show({
      confirmed: async (rating, ratingNote) => {
        this.inspection = await this.inspectionService.accept(this.inspection.id, rating, ratingNote);
        this.toastr.success('Inspection Accepted!');
      },
    });
  }

  async rejectInspection() {
    this.inspection = await this.inspectionService.reject(this.inspection.id);
  }

  getFlagHighlightClass(flag) {
    const score = this.scoresEnabled ? flag.score : 0;
    return {
      red: score >= 100,
      orange: score >= 5 && score < 100,
      amber: score >= 1 && score < 5,
    };
  }

  getQuestionClass(i, question) {
    const score = this.getScore(question);

    return {
      'card-active': this.selectedQuestionIndex === i,
      red: score >= 100,
      orange: score >= 5 && score < 100,
      amber: score >= 1 && score < 5,
    };
  }

  getScore(question) {
    if (!this.scoresEnabled) {
      return 0;
    }

    let score;

    if (Array.isArray(question.answer)) {
      score = question.answer.reduce((sum, ans) => sum + (ans.flaggingScore || 0), 0);
    } else {
      score = question.answer.flaggingScore || 0;
    }

    return score;
  }

  answerChange(ev, change = true) {
    // Update answer
    if (Array.isArray(this.answer)) {
      const answer = this.answer.find(a => a.id === ev.id);
      if (answer) {
        Object.assign(answer, ev);
      }
    } else {
      Object.assign(this.answer, ev);
    }
    this.forceMenu = !this.getCurrentFlags().length && !this.nTotalPictures(this.question);
    this.canForceChange = false;
    if (change) {
      this.refreshAnswers.emit();
    }

    this.question.relatedLoaded = false;
  }

  /**
   * @param preserveRightPanel Pass true when opening edit-flag so the right panel (e.g. Flags) is not forced to Question.
   */
  async openPictureFlag(flag, preserveRightPanel = false) {
    await this.onSelectedQuestionChange(preserveRightPanel);

    this.forceMenu = false;
    // Make sure picture are visible
    setTimeout(() => {
      this.answerPictures.first.openPictureFlag(flag);
    }, 0);
  }

  violations() {
    const flags = this.getCurrentFlags();
    return flags.filter(flag => flag?.subtype?.type?.name === 'Violation' || flag?.subtype?.type?.name === 'Legacy');
  }

  discrepancies() {
    const flags = this.getCurrentFlags();
    return flags.filter(flag => flag?.subtype?.type?.name === 'Discrepancy');
  }

  getCurrentFlags() {
    if (Array.isArray(this.answer)) {
      return this.answer.reduce((flags, answer) => {
        return [...flags, ...(answer?.flags || [])];
      }, []);
    } else {
      return this.answer?.flags || [];
    }
  }

  async editFlag(flag) {
    if (flag.picture) {
      await this.openPictureFlag(flag, true);
    }

    const answer = this.answers.find(a => a.id === flag.answerId);
    this.editFlagModal.show({
      flag,
      answer,
      changed: newFlag => this.updateFlag(newFlag),
      deleted: () => this.removeFlag(flag.id),
    });
  }

  async updateFlag(newFlag) {
    const answerId =
      newFlag?.answerId ??
      (Array.isArray(this.answer)
        ? this.answer.find(a => a.flags?.some(f => f.id === newFlag.id))?.id
        : this.answer?.id);
    const changes = answerId != null ? (await this.answerService.getChanges(answerId)) ?? [] : [];
    if (Array.isArray(this.answer)) {
      this.answer = this.answer.map(answer => {
        if (answer.flags.some(f => f.id === newFlag.id)) {
          const flags = answer.flags.filter(f => f.id !== newFlag.id);
          return {
            ...answer,
            flags: [...flags, newFlag],
            changes: [...changes],
          };
        }
        return answer; // Return the answer unchanged if it doesn't have the flag
      });
    } else {
      const flags = this.answer.flags.filter(f => f.id !== newFlag.id);
      this.answer = {
        ...this.answer,
        flags: [...flags, newFlag],
        changes: [...changes],
      };
    }
    this.question.answer = this.answer;
    this.answer.flaggingScore = this.question.answer.flags.reduce((sum, ans) => sum + (ans.score || 0), 0);
    this.toastr.success('Flag Saved');
    this.answerChange(this.answer);
    this.changeDetector.detectChanges();
  }

  async removeFlag(id: number) {
    const answerIdForChanges = Array.isArray(this.answer)
      ? this.answer.find(a => a.flags?.some(f => f.id === id))?.id
      : this.answer?.id;
    const changes = answerIdForChanges != null ? (await this.answerService.getChanges(answerIdForChanges)) ?? [] : [];

    if (Array.isArray(this.answer)) {
      this.answer = this.answer.map(answer => {
        if (answer.flags.some(f => f.id === id)) {
          const flags = answer.flags.filter(f => f.id !== id);
          return {
            ...answer,
            flags,
            changes,
            flaggingScore: flags.reduce((sum, f) => sum + (f.score || 0), 0),
          };
        }
        return answer;
      });
    } else {
      const flags = this.answer.flags.filter(f => f.id !== id);
      this.answer = {
        ...this.answer,
        flags,
        changes,
      };
    }
    this.question.answer = this.answer;
    if (!Array.isArray(this.answer)) {
      this.answer.flaggingScore = this.answer.flags.reduce((sum, ans) => sum + (ans.score || 0), 0);
    }
    this.toastr.success('Flag Removed');
    this.answerChange(this.answer);
    this.getFlagsAndComments();
    this.changeDetector.detectChanges();

    if (this.contentRight === 'flag') {
      this.contentRight = 'question';
    }
  }

  get canAddNewFlag(): boolean {
    if (!this.answer) {
      return false;
    }
    if (Array.isArray(this.answer)) {
      return this.answer.length > 0;
    }
    return true;
  }

  addFlag(initialType = '') {
    this.resolveAnswerForNewFlag(answer => {
      if (!answer) {
        return;
      }
      this.addFlagModal.show({
        answer,
        initialType,
        confirmed: () => {
          this.toastr.success('Flag Saved');
          this.question.answer = this.answer;
          this.answerChange(this.answer);
          this.getFlagsAndComments();
        },
        canceled: () => {},
      });
    });
  }

  private resolveAnswerForNewFlag(callback: (answer: Answer) => void, answers?: Answer[]): void {
    if (!answers) {
      if (!this.answer) {
        return;
      }
      if (!Array.isArray(this.answer)) {
        callback(this.answer);
        return;
      }
      if (this.answer.length === 0) {
        return;
      }
      if (this.answer.length === 1) {
        callback(this.answer[0]);
        return;
      }
      const sorted = [...this.answer].sort((a, b) => (a.buildingIndex ?? 0) - (b.buildingIndex ?? 0));
      this.resolveAnswerForNewFlag(callback, sorted);
      return;
    }

    if (answers.length === 1) {
      callback(answers[0]);
      return;
    }

    const [first, second, ...rest] = answers;
    this.dialog.multipleButton().show({
      message: rest.length
        ? 'Select building for the new flag (or cancel to see more)'
        : 'Select building for the new flag',
      buttonOne: `Building #${first.buildingIndex ?? 1}`,
      buttonTwo: `Building #${second.buildingIndex ?? 2}`,
      buttonOneClicked: () => callback(first),
      buttonTwoClicked: () => callback(second),
      canceled: rest.length ? () => this.resolveAnswerForNewFlag(callback, rest) : undefined,
    });
  }

  buildingIndex(flag) {
    if (this.answer && Array.isArray(this.answer)) {
      const answer = this.answer.find(a => a.id === flag.answerId);
      if (answer) {
        return answer.buildingIndex;
      }
    }
  }

  flagReleased(flag) {
    // wait next tick, so picture's mouseup handler runs first
    setTimeout(async () => {
      const response = await this.answerPictures.first.attachFlagToPicture(flag);

      if (this.question.answer.changes) {
        this.question.answer = {
          ...this.answer,
          changes: response.changes,
        };
      }

      Object.assign(flag, response.updatedFlag);
    }, 0);
  }

  openChangelog() {
    this.answerForm.accordion.active = 'changes';
  }

  async bulkUpdateAnswers(type: 'item' | 'section' | 'inspection', status: number) {
    let questions;
    const questionsWithAnswers = [];
    if (type === 'item') {
      questions = this.sections.map(section => section.questions).reduce((acc, val) => acc.concat(val), []);
    } else if (type === 'section') {
      questions = this.questions;
    } else if (type === 'inspection') {
      questions = this.items
        .map(item => item.sections)
        .reduce((acc, val) => acc.concat(val), [])
        .map(section => section.questions)
        .reduce((acc, val) => acc.concat(val), []);
    }

    for (const question of questions) {
      if (Array.isArray(question.answer)) {
        for (const answer of question.answer) {
          if (answer) {
            questionsWithAnswers.push({ question: question, answer: answer });
          }
        }
      } else {
        if (question.answer) {
          questionsWithAnswers.push({ question: question, answer: question.answer });
        }
      }
    }
    const questionsWithoutAnswers = questionsWithAnswers.filter(
      qa => !this.isPictureOrLocationOnly(qa.question) && qa.answer.answer === '',
    );

    const answersRejected = questionsWithAnswers.filter(qa => qa.answer.status === 1);
    if (status === 2 && questionsWithoutAnswers.length > 0) {
      await this.unansweredQuestionsDialog();
      return;
    }

    if (status === 1 && type === 'inspection') {
      await this.rejectInspectionDialog(status, type, questions, questionsWithAnswers, answersRejected);
      return;
    }

    if (status === 2 && type === 'inspection' && answersRejected.length > 0) {
      await this.rejectedQuestionsDialog(status, type, questions, questionsWithAnswers, answersRejected);
      return;
    }

    await this.changeStatusDialog(status, type, questions, questionsWithAnswers);
    return;
  }

  async unansweredQuestionsDialog() {
    const message = `<h5>We cannot mark all questions as “Accepted“ as some questions don’t have an answer provided.
              Please provide the answers before continuing.</h5>`;
    this.dialog.multipleButton().show({
      maxWidth: true,
      messageHtml: message,
      cancelColor: '#dc3e3e',
      buttonOne: 'Show unanswered questions',
      buttonOneClicked: () => {
        this.openFilters();
        this.refreshAnswers.emit();
      },
    });
  }

  countAnsweredAnswers(answers) {
    if (!answers) return 0;

    const unansweredCount = answers.filter(obj => {
      return (
        obj.answer === '' &&
        obj.question.inspectorVisibility == 'never' &&
        (obj.question.type == 'free-text' || obj.question.type == 'pick-list')
      );
    });
    return answers.length - unansweredCount.length;
  }

  openFilters() {
    this.navigation.applyShortcut('all-unanswered');
    this.navigation.applyFilters();
  }

  async rejectInspectionDialog(status, type, questions, questionsWithAnswers, answersRejected) {
    let messageHtml = `<h5>You can’t reject the inspection unless at least 1 answer is rejected!</h5>`;
    let rejectPartlyButton = null;
    let rejectAllButton = null;
    if (answersRejected.length) {
      messageHtml = `<h5>Rejected questions will be presented back to the inspector to correct.
                    You currently have ${this.totalRejected} questions rejected. You can choose to mark all questions as rejected</h5>`;
      rejectPartlyButton = 'Reject inspection as is';
      rejectAllButton = 'Mark all questions as rejected first';
    }
    this.dialog.multipleButton().show({
      messageHtml,
      maxWidth: true,
      buttonOneColor: '#dc3e3e',
      buttonTwoColor: '#dc3e3e',
      buttonOne: rejectPartlyButton,
      buttonTwo: rejectAllButton,
      cancelColor: '#dc3e3e',
      buttonOneClicked: async () => {
        await this.rejectInspection();
      },
      buttonTwoClicked: async () => {
        await this.changeAnswerStatus(status, type, questions, questionsWithAnswers);
        await this.rejectInspection();
        this.refreshAnswers.emit();
        this.updateProgressBars();
      },
    });
  }

  async rejectedQuestionsDialog(status, type, questions, questionsWithAnswers, answersRejected) {
    const messageHtml = `<h5>There are ${answersRejected.length} rejected answers! To accept this inspection, please accept these answers first!</h5>`;
    this.dialog.multipleButton().show({
      messageHtml,
      maxWidth: true,
      buttonOneColor: '#4dbd74',
      buttonOne: 'Accept rejected answers',
      buttonOneClicked: async () => {
        await this.changeAnswerStatus(status, type, questions, questionsWithAnswers);
        await this.acceptInspection();
        this.refreshAnswers.emit();
        this.updateProgressBars();
      },
    });
  }

  async changeStatusDialog(status, type, questions, questionsWithAnswers) {
    const statusMessages = [
      `Do you really want to change the status to not reviewed for the whole ${type}? If you're using filters, those questions will be affected too!`,
      `Do you really want to reject the whole ${type}? If you're using filters, those questions will be affected too!`,
      `Do you really want to accept the whole ${type}? If you're using filters, those questions will be affected too!`,
    ];
    const message = statusMessages[status];
    let buttonOne;
    let buttonOneColor;
    if (status === 1) {
      buttonOneColor = '#dc3e3e';
      buttonOne = 'Reject';
    } else if (status === 2) {
      buttonOneColor = '#4dbd74';
      buttonOne = 'Accept';
    } else {
      buttonOneColor = '#2763cd';
      buttonOne = 'Reset';
    }

    this.dialog.multipleButton().show({
      message,
      buttonOne,
      buttonOneColor,
      cancelColor: '#dc3e3e',
      buttonOneClicked: async () => {
        try {
          await this.changeAnswerStatus(status, type, questions, questionsWithAnswers);
          if (status === 2 && type === 'inspection') {
            await this.acceptInspection();
          }
          this.refreshAnswers.emit();
          this.updateProgressBars();
        } catch (err) {
          console.log('error', err);
        }
      },
    });
  }

  async changeAnswerStatus(status, type, questions, questionsWithAnswers) {
    const answerIds = questionsWithAnswers.map(qa => qa.answer.id);
    await this.answerService.bulkEditStatus(answerIds, status);
    this.toastr.success(`${startCase(camelCase(type))} updated!`);
    for (const question of questions) {
      if (Array.isArray(question.answer)) {
        for (const answer of question.answer) {
          if (answer) {
            answer.status = status;
          }
        }
      } else {
        if (question.answer) {
          question.answer.status = status;
        }
      }
    }
  }

  isPictureOrLocationOnly(question) {
    return question && (question.type === 'picture-only' || question.type === 'location-only');
  }

  goToLocations() {
    if (this.nLocations) this.router.navigate([`/inspection/review/locations/${this.inspection.id}`]);
  }
}
